import { NgTemplateOutlet } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject } from '@angular/core';
import frenchi18n from '@assets/i18n/fr.json';
import { WaitOnWebauthNotification } from '@core/usecase/waitOnWebauthNotification';
import { TranslateModule } from '@ngx-translate/core';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { GetAccounts } from '@core/usecase/getAccounts';
import { QrCodeComponent } from '@shared/components/qr-code/qr-code.component';
import { isProbablyPhone } from '@shared/models/crossDeviceCapability';
import { isWebauthQrCodeEnabledToken } from 'src/conf/di/angularProviderMapper';
import { WaitOnExternalBankLoginVM } from './wait-on-external-bank-login.viewmodel';

@Component({
  selector: 'app-wait-on-external-bank-login',
  templateUrl: './wait-on-external-bank-login.component.html',
  standalone: true,
  imports: [TranslateModule, QrCodeComponent, NgTemplateOutlet],
})
export class WaitOnExternalBankLoginComponent implements OnInit, OnDestroy {
  private readonly storeService = inject(StoreService);
  private readonly processPageParams = this.storeService.select('PROCESS_PAGE_PARAMS');
  readonly regionalBankName = computed(() => this.processPageParams()?.regionalBankName);
  readonly selectedBank = this.storeService.selectOrThrow('SELECTED_BANK');
  readonly webauthBankUrl = this.storeService.select('WEBAUTH_BANK_URL');
  /*
    Whether this journey offered the user a choice between this computer and
    their phone — the qr code option (mirror of ProcessVM.shouldOfferQrCode).
    Once offered and made, that choice is final: the qr code has no place next
    to the spinner.
  */
  private readonly wasQrCodeOffered = inject(isWebauthQrCodeEnabledToken) && !isProbablyPhone();
  /*
    The bank url is the only thing the qr code needs: it is fetched by the
    origin page as soon as the qr code is offered (server completion is the
    default), and it is what the qr code encodes.
  */
  private readonly isQrCodeAvailable = computed(() => this.wasQrCodeOffered && this.webauthBankUrl() != null);
  /*
    No bank tab has been opened yet: the user picks between this computer and
    their phone first. Set by the process view model on bank choice, cleared as
    soon as the tab is opened.
  */
  private readonly isAwaitingDeviceChoice = this.storeService.select('WEBAUTH_AWAITING_DEVICE_CHOICE');
  /*
    The choice only makes sense once the bank url answered: displaying it
    earlier would offer a qr code slot that pops in a moment later.
  */
  readonly shouldDisplayDeviceChoice = computed(
    () => this.isAwaitingDeviceChoice() === true && this.webauthBankUrl() != null
  );
  readonly isPreparingDeviceChoice = computed(
    () => this.isAwaitingDeviceChoice() === true && this.webauthBankUrl() == null
  );
  /*
    The bank tab is open (or the relay drives it) and this screen only waits:
    the one moment the user may want to bail out to another bank. Not offered
    while they are still choosing a device, nor while that choice is prepared.
  */
  readonly isWaitingOnBankTab = computed(() => this.isAwaitingDeviceChoice() !== true);
  /*
    The qr code is one of the two options of the device-choice screen, and only
    lives there: after "continue on this computer" the user has chosen, and the
    qr code goes away with the choice.
  */
  readonly shouldDisplayQrCode = computed(() => this.isQrCodeAvailable() && this.shouldDisplayDeviceChoice());
  private waitOnExternalBankLoginVM: WaitOnExternalBankLoginVM;

  constructor(
    private processEventService: ProcessEventService,
    private waitOnWebauthNotification: WaitOnWebauthNotification,
    private tracker: TrackerService,
    private getAccounts: GetAccounts
  ) {
    this.waitOnExternalBankLoginVM = new WaitOnExternalBankLoginVM(
      this.processEventService,
      this.storeService,
      this.waitOnWebauthNotification,
      this.getAccounts
    );
  }

  async ngOnInit() {
    this.tracker.trackPage({ taggingPage: 'WAIT_ON_EXTERNAL_BANK_LOGIN' });
    await this.waitOnExternalBankLoginVM.onInit();
  }

  ngOnDestroy(): void {
    this.waitOnExternalBankLoginVM.stopPolling();
  }

  backToBankChoice(): void {
    this.tracker.trackClick({
      click: frenchi18n.choose_another_bank,
      taggingPage: 'WAIT_ON_EXTERNAL_BANK_LOGIN',
    });

    this.waitOnExternalBankLoginVM.backToBankChoice();
  }

  continueOnThisDevice(): void {
    this.tracker.trackClick({
      click: frenchi18n.continue_on_this_computer,
      taggingPage: 'WAIT_ON_EXTERNAL_BANK_LOGIN',
    });

    this.waitOnExternalBankLoginVM.continueOnThisDevice();
  }
}
